import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/store/user'
import { accountHomePath } from '@/utils/authNavigation'

import Login from '@/views/Login.vue'

const routes: Array<RouteRecordRaw> = [
  // Public routes
  {
    path: '/',
    name: 'Home',
    // 動態重定向由 navigation guard 處理
    component: Login
  },
  {
    path: '/login',
    name: 'Login',
    component: Login
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPassword.vue')
  },
  {
    path: '/register/affiliate',
    name: 'RegisterAffiliate', 
    component: () => import('@/views/RegisterAffiliate.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/join',
    name: 'PublicRegisterAffiliate',
    component: () => import('@/views/RegisterAffiliate.vue')
  },
  {
    path: '/join/success',
    name: 'AffiliateApplicationSuccess',
    component: () => import('@/views/AffiliateApplicationSuccess.vue')
  },
  {
    path: '/apply',
    name: 'Apply',
    component: () => import('@/views/PublicApply.vue') // 客戶報名頁
  },
  {
    path: '/apply/success',
    name: 'ApplySuccess',
    component: () => import('@/views/ApplySuccess.vue')
  },
  {
    path: '/order-status',
    name: 'OrderLookup',
    component: () => import('@/views/OrderLookup.vue')
  },
  {
    path: '/terms',
    name: 'TermsOfService',
    component: () => import('@/views/TermsOfService.vue')
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/PrivacyPolicy.vue')
  },
  {
    path: '/r/:refCode',
    name: 'ReferralRedirect',
    redirect: (to) => {
      const refCode = to.params.refCode as string
      return {
        path: '/apply',
        query: {
          ...to.query,
          ref: refCode,
          utm_source: to.query.utm_source || 'affiliate',
          utm_medium: to.query.utm_medium || 'referral',
        }
      }
    }
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: () => import('@/views/ChangePassword.vue'),
    meta: { requiresAuth: true }
  },
  
  // Affiliate routes (兼職人員)
  {
    path: '/affiliate',
    redirect: '/affiliate/dashboard'
  },
  {
    path: '/affiliate/dashboard',
    name: 'AffiliateDashboard',
    component: () => import('@/views/AffiliateDashboard.vue'),
    meta: { requiresAuth: true, requiresRole: 'affiliate' }
  },
  {
    path: '/affiliate/products',
    name: 'AffiliateProducts',
    component: () => import('@/views/AffiliateProducts.vue'),
    meta: { requiresAuth: true, requiresRole: 'affiliate' }
  },
  {
    path: '/affiliate/products/:id',
    name: 'AffiliateProductDetail',
    component: () => import('@/views/AffiliateProductDetail.vue'),
    meta: { requiresAuth: true, requiresRole: 'affiliate' }
  },
  {
    path: '/affiliate/customers',
    name: 'AffiliateCustomers',
    component: () => import('@/views/AgentLeads.vue'),
    meta: { requiresAuth: true, requiresRole: 'affiliate' }
  },
  {
    path: '/affiliate/bookings',
    name: 'AffiliateBookings',
    component: () => import('@/views/AgentLeads.vue'),
    meta: { requiresAuth: true, requiresRole: 'affiliate' }
  },
  {
    path: '/affiliate/downlines',
    name: 'AffiliateDownlines',
    redirect: '/affiliate/dashboard'
  },
  {
    path: '/partner/:slug',
    name: 'PublicAgencyStorefront',
    component: () => import('@/views/PublicAgencyStorefront.vue'),
    props: true
  },
  {
    path: '/affiliate/links',
    name: 'AffiliateLinks',
    component: () => import('@/views/AgentLinks.vue'),
    meta: { requiresAuth: true, requiresRole: 'affiliate' }
  },
  {
    path: '/affiliate/profile',
    name: 'AffiliateProfile',
    component: () => import('@/views/AgentPayouts.vue'),
    meta: { requiresAuth: true, requiresRole: 'affiliate' }
  },
  
  // Admin routes
  {
    path: '/admin',
    redirect: '/admin/affiliates'
  },
  {
    path: '/admin/affiliates',
    name: 'AdminAffiliates',
    component: () => import('@/views/AdminAffiliates.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/admin/affiliates/:id',
    name: 'AdminAffiliateDetail',
    component: () => import('@/views/AdminAffiliates.vue'), // 詳細頁面
    meta: { requiresAuth: true, requiresRole: 'admin' },
    props: true
  },
  {
    path: '/admin/contacts',
    name: 'AdminContacts',
    component: () => import('@/views/AdminContacts.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/admin/orders',
    name: 'AdminOrders',
    component: () => import('@/views/AdminCustomers.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/admin/products',
    name: 'AdminProducts',
    component: () => import('@/views/AdminProducts.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/sites/:tenantId',
    name: 'PublicTenantSite',
    component: () => import('@/views/PublicTenantSite.vue')
  },
  {
    path: '/website-preview',
    name: 'WebsitePreviewFrame',
    component: () => import('@/views/WebsitePreviewFrame.vue')
  },
  {
    path: '/admin/websites',
    redirect: '/admin/tenants'
  },
  {
    path: '/admin/websites/:tenantId',
    redirect: to => `/admin/tenants/${String(to.params.tenantId)}/website`
  },
  {
    path: '/admin/tenants/:tenantId/website',
    name: 'AdminTenantWebsite',
    component: () => import('@/views/AdminWebsiteEditor.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/admin/payouts',
    name: 'AdminPayouts',
    component: () => import('@/views/AdminSettlement.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/admin/applications',
    name: 'AdminApplications',
    component: () => import('@/views/AdminApplications.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', requiresFullAdmin: true }
  },
  {
    path: '/admin/funnel',
    name: 'AdminFunnel',
    component: () => import('@/views/AdminFunnel.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/admin/users',
    name: 'AdminUserManagement',
    component: () => import('@/views/AdminUserManagement.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/admin/settings',
    name: 'AdminSettings',
    component: () => import('@/views/AdminSettings.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },
  {
    path: '/admin/tenants',
    name: 'AdminTenants',
    component: () => import('@/views/AdminTenants.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' }
  },

  // Licensed-agency tenant routes
  {
    path: '/agency',
    redirect: '/agency/dashboard'
  },
  {
    path: '/agency/dashboard',
    name: 'AgencyDashboard',
    component: () => import('@/views/AgencyDashboard.vue'),
    meta: { requiresAuth: true, requiresRole: 'tenant' }
  },
  {
    path: '/agency/products',
    name: 'AgencyProducts',
    component: () => import('@/views/AgencyProducts.vue'),
    meta: { requiresAuth: true, requiresRole: 'tenant' }
  },
  {
    path: '/agency/inquiries',
    name: 'AgencyInquiries',
    component: () => import('@/views/AgencyInquiries.vue'),
    meta: { requiresAuth: true, requiresRole: 'tenant' }
  },
  {
    path: '/agency/orders',
    name: 'AgencyOrders',
    component: () => import('@/views/AgencyOrders.vue'),
    meta: { requiresAuth: true, requiresRole: 'tenant' }
  },
  {
    path: '/agency/settings',
    name: 'AgencySettings',
    component: () => import('@/views/AgencySettings.vue'),
    meta: { requiresAuth: true, requiresRole: 'tenant' }
  },
  {
    path: '/agency/website',
    name: 'AgencyWebsite',
    component: () => import('@/views/AdminWebsiteEditor.vue'),
    meta: { requiresAuth: true, requiresRole: 'tenant', requiresTenantManager: true }
  },
  
  // Fallback route
  {
    path: '/:pathMatch(.*)*',
    redirect: '/apply'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  
  // Wait for auth to initialize
  if (userStore.loading) {
    await userStore.initAuth()
  }
  
  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    if (!userStore.isLoggedIn) {
      // Redirect to login page
      next({ name: 'Login', query: { redirect: to.fullPath } })
      return
    }
    
    // Check for first login - redirect to change password (except if already on change-password page)
    if (to.name !== 'ChangePassword' && userStore.userProfile?.isFirstLogin) {
      next({ name: 'ChangePassword', query: { firstLogin: 'true' } })
      return
    }
    
    // Check role requirements
    if (to.meta.requiresRole) {
      const requiredRole = to.meta.requiresRole as string
      
      if (requiredRole === 'admin' && !userStore.isAdmin) {
        // Redirect to affiliate dashboard if user is affiliate, or login if no role
        if (userStore.isAffiliate) {
          next({ name: 'AffiliateDashboard' })
        } else {
          next({ name: 'Login' })
        }
        return
      }

      if (requiredRole === 'admin') {
        if (!userStore.canAccessAdmin(to.path) && userStore.currentUser) {
          await userStore.loadUserProfile(userStore.currentUser.uid)
        }
        if (!userStore.canAccessAdmin(to.path)) {
          const destination = userStore.adminHome
          next(destination === to.fullPath ? '/login?access=unavailable' : destination)
          return
        }
      }
      
      if (requiredRole === 'affiliate' && !userStore.isAffiliate) {
        // Redirect to admin if user is admin, or login if no role
        if (userStore.isAdmin) {
          next({ name: 'AdminAffiliates' })
        } else {
          next({ name: 'Login' })
        }
        return
      }

      if (requiredRole === 'tenant' && !userStore.isTenantMember) {
        next(userStore.isAdmin ? { name: 'AdminAffiliates' } : { name: 'Login' })
        return
      }

      if (to.meta.requiresTenantManager && !userStore.canManageTenant) {
        next({ name: 'AgencyDashboard' })
        return
      }
      
      // Check if affiliate is approved for affiliate routes
      if (requiredRole === 'affiliate' && userStore.isAffiliate && !userStore.isApprovedAffiliate) {
        // Show pending approval message or redirect to a waiting page
        // For now, allow access but the views should handle the pending state
      }
    }
  }
  
  // Never silently bounce an unresolved admin profile through login back to the same page.
  if (to.name === 'Login' && to.query.access === 'unavailable') {
    next()
    return
  }
  if (to.name === 'Login' && userStore.isLoggedIn) {
    if (userStore.userProfile?.isFirstLogin) {
      next({ name: 'ChangePassword', query: { firstLogin: 'true' } })
      return
    }
    const destination = accountHomePath(userStore)
    if (destination !== '/login') { next(destination); return }
  }
  if (to.path === '/') {
    if (userStore.isLoggedIn && userStore.userProfile?.isFirstLogin) {
      next({ name: 'ChangePassword', query: { firstLogin: 'true' } })
    } else next(userStore.isLoggedIn ? accountHomePath(userStore) : '/login')
    return
  }
  
  next()
})

export default router
